import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import PermissionGuard from './components/PermissionGuard';
import Login from './components/Login';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Inventory from './components/Inventory';
import Orders from './components/Orders';
import ExtOrders from './components/ExtOrders';
import OrderDetail from './components/OrderDetail';
import Catalog from './components/Catalog';
import Accounts from './components/Accounts';
import Users from './components/Users';
import PendingApprovals from './components/PendingApprovals';
import WarehouseReceipts from './components/WarehouseReceipts';
import BOLs from './components/BOLs';
import RateCalculator from './components/RateCalculator';
import Invoices from './components/Invoices';
import Profile from './components/Profile';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Shared set of child routes — used for both "/" and "/:tenantPrefix"
// PermissionGuard is applied on ALL routes so tenant-prefixed routes
// are equally protected (security fix: previously tenant routes had no guards).
function AppRoutes() {
  return (
    <>
      <Route index element={<Navigate to="dashboard" replace />} />

      <Route path="dashboard" element={<Dashboard />} />

      <Route path="bols" element={
        <PermissionGuard module="transportation">
          <BOLs />
        </PermissionGuard>
      } />

      <Route path="rate-calculator" element={
        <PermissionGuard module="transportation">
          <RateCalculator />
        </PermissionGuard>
      } />

      <Route path="inventory" element={
        <PermissionGuard module="inventory">
          <Inventory />
        </PermissionGuard>
      } />

      <Route path="orders" element={
        <PermissionGuard module="inventory">
          <Orders />
        </PermissionGuard>
      } />

      <Route path="orders/:orderId" element={
        <PermissionGuard module="inventory">
          <OrderDetail />
        </PermissionGuard>
      } />

      <Route path="ext-orders" element={
        <PermissionGuard module="ext_orders">
          <ExtOrders />
        </PermissionGuard>
      } />

      <Route path="warehouse-receipts" element={
        <PermissionGuard module="warehouse_receipts">
          <WarehouseReceipts />
        </PermissionGuard>
      } />

      <Route path="invoices" element={
        <PermissionGuard module="invoices">
          <Invoices />
        </PermissionGuard>
      } />

      <Route path="catalog" element={
        <PermissionGuard module="inventory">
          <Catalog />
        </PermissionGuard>
      } />

      <Route path="accounts" element={
        <PermissionGuard module="accounts">
          <Accounts />
        </PermissionGuard>
      } />

      <Route path="users" element={
        <PermissionGuard module="admin">
          <Users />
        </PermissionGuard>
      } />

      <Route path="pending-approvals" element={
        <PermissionGuard module="inventory" permission="approve">
          <PendingApprovals />
        </PermissionGuard>
      } />

      <Route path="profile" element={<Profile />} />

      {/* Catch-all: redirect to dashboard */}
      <Route path="*" element={<Navigate to="dashboard" replace />} />
    </>
  );
}

function App() {
  return (
    <Router>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { background: '#1e293b', color: '#f1f5f9', borderRadius: '8px' },
          success: {
            duration: 3000,
            style: { background: '#064e3b', color: '#d1fae5' },
            iconTheme: { primary: '#10b981', secondary: '#d1fae5' },
          },
          error: {
            duration: 5000,
            style: { background: '#450a0a', color: '#fecaca' },
            iconTheme: { primary: '#ef4444', secondary: '#fecaca' },
          },
        }}
      />
      <TenantProvider>
        <AuthProvider>
          <Routes>
            {/* Public login routes */}
            <Route path="/login"                  element={<Login />} />
            <Route path="/:tenantPrefix/login"    element={<Login />} />

            {/* Root protected routes */}
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              {AppRoutes()}
            </Route>

            {/* Tenant-prefixed protected routes — now also guarded */}
            <Route path="/:tenantPrefix" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              {AppRoutes()}
            </Route>
          </Routes>
        </AuthProvider>
      </TenantProvider>
    </Router>
  );
}

export default App;
